import { Features, selectHasFeature } from "@/store/features/features-slice";
import { ComponentsToDisplay } from "@/store/measurement-tool-slice";
import { useAppSelector } from "@/store/store-hooks";
import { HtmlProps } from "@react-three/drei/web/Html";
import { MutableRefObject, useMemo, type JSX } from "react";
import { Material, Matrix3, Matrix4, Vector3 } from "three";
import { computeMeasurementSegments } from "./measure-utils";
import { TwoPointMeasureSegment } from "./two-point-segment-renderer";

type MultiSegmentRendererProps = {
  /** The list of points describing the polyline local to the reference system defined by the `worldMatrix` */
  points: Vector3[];
  /** A flag specifying if the polyline is closed */
  isClosed: boolean;
  /** A flag specifying if the measurement is the current active one */
  isActive: boolean;
  /** A ref to the parent HTML element to which all labels will be attached */
  labelContainer: MutableRefObject<HTMLElement>;
  /** The HTML property for the pointer events on the HTML labels */
  labelsPointerEvents: HtmlProps["pointerEvents"];
  /** The world matrix of the whole measurement */
  worldMatrix: Matrix4;
  /** A flag for making dashed lines */
  dashed?: boolean;
  /** Whether depth testing should be used to render the segment */
  depthTest?: Material["depthTest"];
  /**
   * Callback providing center position of the segment when a label is clicked
   *
   * @param position of the segment center
   * @param firstPointIndex index of the first point of the segment
   */
  onClick?(position: Vector3, firstPointIndex: number): void;
  /** True if the label should be rendered */
  isLabelVisible: boolean;
  /** Component to display for 2-points line */
  componentsToDisplay?: ComponentsToDisplay;
  /** Flag indicating the value display is an approximation */
  isApproximation?: boolean;
};

/** @returns A component to render a polyline that, optionally, could be closed, where each segment have a label showing the length */
export function MultiSegmentRenderer({
  points,
  isClosed,
  isActive,
  labelContainer,
  labelsPointerEvents,
  worldMatrix,
  dashed,
  depthTest,
  onClick,
  isLabelVisible,
  componentsToDisplay,
  isApproximation,
}: MultiSegmentRendererProps): JSX.Element {
  const signedAlongAxis = useAppSelector(
    selectHasFeature(Features.DisplaySignedDeviations),
  );

  // Matrix to convert from points local coordinates system to world
  const matrix = useMemo(
    () => new Matrix3().setFromMatrix4(worldMatrix),
    [worldMatrix],
  );

  const listSegments = useMemo(
    () =>
      computeMeasurementSegments({
        points,
        isClosed,
        componentsToDisplay,
        matrix,
        signedAlongAxis,
      }),
    [points, isClosed, componentsToDisplay, matrix, signedAlongAxis],
  );

  return (
    <>
      {listSegments.map((segment, i) => (
        <TwoPointMeasureSegment
          key={i}
          main
          start={segment.start}
          end={segment.end}
          length={segment.length}
          index={0}
          live={false}
          visible
          isMeasurementActive={isActive}
          isLabelActive={isActive}
          labelContainer={labelContainer}
          labelsPointerEvents={labelsPointerEvents}
          onClick={() => onClick?.(segment.labelPosition, i)}
          dashed={dashed}
          depthTest={depthTest}
          labelPosition={segment.labelPosition}
          prefix={segment.prefix}
          isLabelVisible={isLabelVisible}
          isApproximation={isApproximation}
        />
      ))}
    </>
  );
}
